import { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  authorizeChange,
  changesShellQuery,
  changesTableQuery,
  type ChangeLink,
  type ChangeRow,
  type FilterCounts,
  type Option,
} from '../../api/admChanges'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, AdmField, WindowPicker } from './AdmFilterBar'

/**
 * ADM -> Changes, din `views/version-3/softsee/adm/changes/index.php` si din
 * tabelul lui din `public/js/adm.js`.
 *
 * DOUA FELURI DE SCHIMBARE, DOUA FILE, si stau LANGA NUMELE ecranului:
 * "On equipment" e ce s-a modificat PE o masina si are judecata pe fiecare rand
 * (avea voie sau nu); "Between items" e o legatura care a aparut sau a incetat,
 * si acolo n-ar avea ce sa scrie in coloana "Authorised" - un "nu" pe un rand
 * de legatura s-ar citi ca o neregula.
 *
 * Filele stau deasupra cifrei fiindca ii schimba INTELESUL: cate n-a autorizat
 * nimeni e o cifra despre prima fila.
 *
 * Foile sunt cele ale portalului (`public/css/adm.css`), deci pastilele,
 * banda de sus si lista arata la fel ca inainte de mutare.
 */

const STYLES = ['css/adm.css']

/** Textul unei celule, scapat - `$('<div>').text(x).html()` din adm.js. */
function esc(value: unknown): string {
  const box = document.createElement('div')
  box.textContent = String(value ?? '')
  return box.innerHTML
}

/** Eticheta unei optiuni, cu numarul de randuri dupa ea. */
function withCount(option: Option, bucket: Record<string, number> | undefined): string {
  if (option.value === '') return option.label
  if (!bucket) return option.label
  return `${option.label} (${Object.prototype.hasOwnProperty.call(bucket, option.value) ? bucket[option.value] : 0})`
}

/**
 * Coloanele listei, cu acelasi HTML pe care il scria `adm.js`.
 *
 * Coloana clientului lipseste: ecranele astea arata UN client (cel din antet),
 * deci ar fi repetat acelasi nume pe fiecare rand - `admAllCompanies()` din
 * portal raspunde de mult `false`.
 */
function changeColumns(canWrite: boolean): TableColumn[] {
  return [
    {
      index: 0,
      title: 'When',
      orderable: true,
      visible: true,
      className: 'adm-nowrap',
      html: (row) => esc(row.detected_at),
    },
    {
      index: 1,
      title: 'Item',
      orderable: true,
      visible: true,
      html: (row) => {
        const r = row as unknown as ChangeRow
        /* Un aparat abia descoperit n-are inca element de configuratie - de-aia
           numele lui sta in `new_value`. Fara asta, randul "New device" ar fi
           aratat gol tocmai in coloana care spune despre CE e vorba. */
        if (!r.ci_id) return esc(r.ci_name || r.new_value || '')
        /* Fereastra noua, ca peste tot in CMDB: cine trece prin lista de
           schimbari nu vrea s-o piarda pentru un element. */
        return `<a href="/app/cmdb/view/${r.ci_id}" target="_blank" rel="noopener">${esc(r.ci_name || '')}</a>`
      },
    },
    {
      index: 2,
      title: 'Severity',
      orderable: false,
      visible: true,
      html: (row) => {
        const r = row as unknown as ChangeRow
        const tone = r.severity === 'critical' ? 'adm-pill-danger' : r.severity === 'major' ? 'adm-pill-warning' : ''
        return `<span class="adm-pill ${tone}">${esc(r.severity_label || '')}</span>`
      },
    },
    {
      index: 3,
      title: 'What',
      orderable: true,
      visible: true,
      html: (row) => {
        const r = row as unknown as ChangeRow
        /* Sub categorie: ce s-a intamplat, si cu ce fel de program. Pe ecranul
           programelor categoria e "Software" pe FIECARE rand - adica o coloana
           care spune pe ce ecran esti, nu despre ce e randul. */
        const bits = [r.software_class_label, r.software_kind_label].filter((b) => b)
        const line = esc(r.type_label || '')
        if (bits.length === 0) return line
        return `${line}<div class="adm-muted" style="font-size:11px">${esc(bits.join(' · '))}</div>`
      },
    },
    {
      index: 4,
      title: 'Change',
      orderable: false,
      visible: true,
      html: (row) => changeCell(row as unknown as ChangeRow),
    },
    { index: 5, title: 'Source', orderable: false, visible: true, html: (row) => esc((row as unknown as ChangeRow).source_label) },
    {
      index: 6,
      title: 'Authorised',
      orderable: true,
      visible: true,
      html: (row) => {
        const r = row as unknown as ChangeRow
        if (Number(r.authorized) !== 1) return '<span class="adm-pill adm-pill-bad">no</span>'
        /* Care fereastra sau care tichet a permis-o e chiar valoarea coloanei -
           "yes" singur nu e ceva ce poate urmari un auditor. */
        const by = r.window_name ? esc(r.window_name) : r.authorized_by_ticket_id ? `ticket ${r.authorized_by_ticket_id}` : ''
        return `<span class="adm-pill adm-pill-ok">yes</span> <span class="adm-muted" style="font-size:11px">${by}</span>`
      },
    },
    {
      index: 7,
      title: '',
      orderable: false,
      visible: true,
      width: 110,
      className: 'text-right adm-nowrap',
      html: (row) => {
        const r = row as unknown as ChangeRow
        if (!canWrite || Number(r.authorized) === 1) return ''
        return `<button type="button" class="btn btn-success btn-xs admAuthorize" data-id="${r.ID}">Authorise</button>`
      },
    },
  ]
}

/**
 * Coloana "Change": numele lucrului schimbat, si valorile de o parte si de alta.
 *
 * Fara nume, un rand de Software Changes scria "152.0.4191.53" si atat: aflai
 * ca s-a instalat ceva cu versiunea aia, dar nu si CE.
 */
function changeCell(r: ChangeRow): string {
  const newText = typeof r.new_value_text === 'string' ? r.new_value_text : r.new_value || ''
  const oldText = typeof r.old_value_text === 'string' ? r.old_value_text : r.old_value || ''

  let text = newText
  /* O redimensionare are amandoua capetele (2 cores -> 8 cores). Numai numarul
     nou ar fi aratat ca un fapt despre masina, nu ca o schimbare. */
  if (oldText && newText && !String(newText).includes('→') && !String(newText).includes(oldText)) {
    text = `${oldText} → ${newText}`
  }
  /* Ce a DISPARUT isi scrie tot doua capete: randul de dezinstalare are
     `new_value` gol, deci celula ramanea cu numele si nimic langa el. */
  if (text === '' && oldText) text = `${oldText} → removed`

  const what = r.subject_name ? String(r.subject_name).trim() : ''
  if (what !== '') {
    return `<strong>${esc(what)}</strong>${text ? ` <span class="adm-muted">${esc(text)}</span>` : ''}`
  }

  /* REZERVA: propozitia din jurnalul aparatului. Se cauta VALORILE in fraza, nu
     textul compus mai sus - fraza scrie "updated from 18.7.5.0 to 18.2.4.0",
     iar noi am fi cautat "18.7.5.0 -> 18.2.4.0", acelasi lucru spus altfel. */
  const said = r.source_message ? String(r.source_message).trim() : ''
  if (said !== '') {
    const already = [newText, oldText].some((part) => part && said.includes(String(part)))
    const extra = text && !already ? ` <span class="adm-muted">${esc(text)}</span>` : ''
    return `${esc(said)}${extra}`
  }

  return esc(text)
}

/** Fila "Between items": zeci de randuri, randate dintr-o data. */
function LinksPane({ links, kinds }: { links: ChangeLink[]; kinds: Option[] }) {
  const [shown, setShown] = useState(10)
  const label = useMemo(() => new Map(kinds.map((k) => [k.value, k.label])), [kinds])

  if (links.length === 0) {
    return (
      <p className="adm-muted" style={{ padding: '14px 0' }}>
        <i className="fas fa-info-circle"></i> No connection has appeared or stopped in this period.
        <span style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
          Connections come from the flows the agent reports, turned into relations by the correlator. A relation has to be
          seen often enough, and with enough confidence, before it is announced &mdash; see the thresholds.
        </span>
      </p>
    )
  }

  return (
    <>
      <table className="table table-hover adm-list ch-links" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 150 }}>When</th>
            <th style={{ width: 100 }}>What</th>
            <th>Between</th>
            <th style={{ width: 140 }}>Relation</th>
            <th style={{ width: 170 }}>Customer</th>
            <th style={{ width: 130 }}>Told anybody</th>
          </tr>
        </thead>
        <tbody>
          {links.slice(0, shown).map((link) => (
            <tr key={link.ID}>
              <td className="adm-muted adm-nowrap">{link.detected_at}</td>
              <td>
                <span className={`adm-pill ${link.change === 1 ? 'adm-pill-ok' : 'adm-pill-warn'}`}>
                  {label.get(String(link.change)) ?? '?'}
                </span>
              </td>
              <td>
                <a href={`/app/cmdb/view/${link.sourceCiId}`} target="_blank" rel="noopener">
                  {link.sourceName || '?'}
                </a>{' '}
                <span className="adm-muted">&rarr;</span>{' '}
                <a href={`/app/cmdb/view/${link.targetCiId}`} target="_blank" rel="noopener">
                  {link.targetName || '?'}
                </a>
                <div className="adm-muted" style={{ fontSize: 11 }}>
                  seen {link.observations}&times;, {link.confidence}% sure, last {link.lastSeen}
                </div>
              </td>
              <td className="adm-muted">{link.relationName || '—'}</td>
              <td className="adm-muted">{link.companyName || '—'}</td>
              <td>
                {link.notified === 1 ? (
                  <span className="label label-success">sent</span>
                ) : (
                  /* "Nu inca" nu e o defectiune: instiintarea pleaca la ora fixa,
                     in rezumat. Devine una cand ramane asa - de-aia se si vede. */
                  <span className="adm-muted">not yet</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {shown < links.length ? (
        <button type="button" className="btn btn-default btn-sm" onClick={() => setShown(links.length)}>
          Show all {links.length}
        </button>
      ) : null}
    </>
  )
}

export function ChangesPage({ scope = '' }: { scope?: string }) {
  const shellData = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shellData.data?.version ?? '')

  const [days, setDays] = useState(30)
  const [pane, setPane] = useState<'items' | 'links'>('items')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [authorized, setAuthorized] = useState('0')
  const [severity, setSeverity] = useState('')
  const [type, setType] = useState('')
  const [kind, setKind] = useState('')
  const [klass, setKlass] = useState('')
  const [pending, setPending] = useState<ChangeRow | null>(null)
  const [ticket, setTicket] = useState('')

  /* Se cauta dupa ce omul se opreste din scris, nu la fiecare litera: lista se
     aduce de pe server, deci fiecare apasare ar fi insemnat o interogare - iar
     raspunsurile s-ar fi intors amestecate. */
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const shell = useQuery(changesShellQuery(scope, days))
  const [state, setState] = useTableState(`admChanges-${scope || 'all'}`, {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'desc' },
    search: '',
  })

  const table = useQuery({
    ...changesTableQuery(scope, {
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderDir: state.order.dir,
      search: debounced,
      days,
      authorized,
      severity,
      type,
      software_kind: kind,
      software_class: klass,
    }),
    placeholderData: keepPreviousData,
  })

  const counts: FilterCounts | undefined = table.data?.filterCounts
  const canWrite = shell.data?.canWrite ?? false
  const columns = useMemo(() => changeColumns(canWrite), [canWrite])

  /* Butonul "Authorise" e scris in HTML-ul celulei, ca in portal: clicul se
     prinde o data, pe tabel. */
  useEffect(() => {
    const host = document.getElementById('admChangesTable')
    if (!host) return
    const onClick = (event: Event) => {
      const target = (event.target as HTMLElement).closest('.admAuthorize')
      if (!target) return
      const id = Number(target.getAttribute('data-id'))
      const row = (table.data?.data ?? []).find((r) => r.ID === id)
      if (row) {
        setTicket('')
        setPending(row)
      }
    }
    host.addEventListener('click', onClick)
    return () => host.removeEventListener('click', onClick)
  }, [table.data])

  const record = async () => {
    if (!pending) return
    try {
      const answer = await authorizeChange(pending.ID, Number(ticket))
      toast(answer.message, 'Changes', 'success')
      setPending(null)
      void table.refetch()
      void shell.refetch()
    } catch (error) {
      toast((error as Error).message, 'Changes', 'error')
    }
  }

  const unauthorized = shell.data?.unauthorized ?? 0
  const tone = unauthorized > 0 ? 'red-intense' : 'green-mode'
  const types = shell.data?.types ?? []

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone={tone}
          icon="fa-exchange-alt"
          title="Changes"
          note="what changed on the equipment, and whether anybody was supposed to change it"
          titleExtra={
            <span className="btn-group ch-toggle" data-toggle="buttons">
              <label className={`btn btn-theme-inverse btn-transparent btn-sm${pane === 'items' ? ' active' : ''}`}>
                <input type="radio" name="chView" value="items" checked={pane === 'items'} onChange={() => setPane('items')} />
                <i className="fas fa-desktop"></i> On equipment
              </label>
              <label className={`btn btn-theme-inverse btn-transparent btn-sm${pane === 'links' ? ' active' : ''}`}>
                <input type="radio" name="chView" value="links" checked={pane === 'links'} onChange={() => setPane('links')} />
                <i className="fas fa-project-diagram"></i> Between items{' '}
                <span className="adm-muted">({shell.data?.links.length ?? 0})</span>
              </label>
            </span>
          }
          stats={[{ value: unauthorized.toLocaleString('en-US'), label: 'not authorised', tone }]}
        />

        <AdmFilterBar>
          <AdmField label="Search" htmlFor="changeSearch" wide>
            <input
              type="text"
              className="form-control input-sm"
              id="changeSearch"
              placeholder="machine name, or what changed"
              autoComplete="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </AdmField>

          <AdmField label="Show" htmlFor="changeState">
            <select className="form-control input-sm" id="changeState" value={authorized} onChange={(e) => setAuthorized(e.target.value)}>
              <option value="0">{withCount({ value: '0', label: 'Not authorised' }, counts?.authorized)}</option>
              <option value="1">{withCount({ value: '1', label: 'Authorised' }, counts?.authorized)}</option>
              <option value="">Everything</option>
            </select>
          </AdmField>

          <AdmField label="Severity" htmlFor="changeSeverity">
            <select className="form-control input-sm" id="changeSeverity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="">Any</option>
              {(shell.data?.severities ?? []).map((s) => (
                <option key={s.value} value={s.value}>
                  {withCount(s, counts?.severity)}
                </option>
              ))}
            </select>
          </AdmField>

          {/* Nu se arata cand ecranul are o singura categorie - pe "Software
              changes" ar fi fost un select cu un singur raspuns. */}
          {types.length > 1 ? (
            <AdmField label="Category" htmlFor="changeType">
              <select className="form-control input-sm" id="changeType" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">Any</option>
                {types.map((t) => (
                  <option key={t.value} value={t.value}>
                    {withCount(t, counts?.types)}
                  </option>
                ))}
              </select>
            </AdmField>
          ) : null}

          {scope === 'software' ? (
            <>
              <AdmField label="What happened" htmlFor="changeKind">
                <select className="form-control input-sm" id="changeKind" value={kind} onChange={(e) => setKind(e.target.value)}>
                  <option value="">Any</option>
                  {(shell.data?.softwareKinds ?? []).map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </AdmField>
              <AdmField label="Kind of software" htmlFor="changeClass">
                <select className="form-control input-sm" id="changeClass" value={klass} onChange={(e) => setKlass(e.target.value)}>
                  <option value="">Any</option>
                  {(shell.data?.softwareClasses ?? []).map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </AdmField>
            </>
          ) : null}

          <WindowPicker windows={shell.data?.windows ?? [1, 7, 30, 90]} value={days} onChange={setDays} />
        </AdmFilterBar>

        <div className="ch-pane" data-pane="links" style={{ display: pane === 'links' ? 'block' : 'none' }}>
          <LinksPane links={shell.data?.links ?? []} kinds={shell.data?.linkKinds ?? []} />
        </div>

        <div className="ch-pane" data-pane="items" style={{ display: pane === 'items' ? 'block' : 'none' }} id="admChangesTable">
          <DataTable
            id="admChanges"
            columns={columns}
            rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
            total={table.data?.recordsTotal ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            onRetry={() => void table.refetch()}
            state={state}
            onState={setState}
            rowId={(r) => String(r.DT_RowId ?? r.ID)}
            emptyHint="Nothing changed in this period."
          />
        </div>
      </div>

      <Modal
        id="admAuthorizeModal"
        open={pending !== null}
        onClose={() => setPending(null)}
        title="Record this change as authorised"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setPending(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-success" id="admAuthorizeConfirm" onClick={() => void record()}>
              Record
            </button>
          </>
        }
      >
        <p className="adm-muted" id="admAuthorizeWhat">
          {pending
            ? `${pending.ci_name || ''} — ${
                pending.subject_name
                  ? `${pending.subject_name} ${pending.new_value_text || pending.new_value || ''}`
                  : pending.source_message || pending.new_value_text || pending.new_value || ''
              }`
            : ''}
        </p>
        <div className="form-group">
          <label htmlFor="admTicketId">Ticket</label>
          <input
            type="number"
            className="form-control"
            id="admTicketId"
            min={1}
            placeholder="e.g. 48213"
            value={ticket}
            onChange={(e) => setTicket(e.target.value)}
          />
          <span className="text-muted small">
            {/* A autoriza fara sa arati spre ceva face din coloana o parere. */}
            The change board approval lives on the ticket, and the ticket number is what an auditor asks for.
          </span>
        </div>
      </Modal>
    </Content>
  )
}
